/**
 * Validation utilities for login form
 * Handles credential type detection (username, email, phone) and password complexity
 */

// Credential types that can be detected from user input
export enum CredentialType {
  Username = 'username',
  Email = 'email',
  Phone = 'phone',
}

// Password requirement status
export interface PasswordRequirements {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

// Password strength levels
export enum PasswordStrength {
  VeryWeak = 0,
  Weak = 1,
  Fair = 2,
  Strong = 3,
  VeryStrong = 4,
}

// Validation result for credentials
export interface CredentialValidationResult {
  type: CredentialType;
  isValid: boolean;
  error?: string;
  normalizedValue?: string;
}

// Validation result for password
export interface PasswordValidationResult {
  requirements: PasswordRequirements;
  strength: PasswordStrength;
  strengthLabel: string;
  isValid: boolean;
}

/**
 * Detect the type of credential from user input
 * @param input - The user input string
 * @returns The detected credential type
 */
export function detectCredentialType(input: string): CredentialType {
  const trimmed = input.trim();

  // Email detection: contains '@'
  if (trimmed.includes('@')) {
    return CredentialType.Email;
  }

  // Phone detection: starts with '+' or has 10-15 digits with >60% digit ratio
  const digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('+') || (digits.length >= 10 && digits.length <= 15)) {
    const digitRatio = digits.length / trimmed.length;
    if (digitRatio > 0.6) {
      return CredentialType.Phone;
    }
  }

  // Default: Username
  return CredentialType.Username;
}

/**
 * Validate an email address
 * @param email - The email to validate
 * @returns Validation result with error message if invalid
 */
export function validateEmail(email: string): { isValid: boolean; error?: string } {
  const trimmed = email.trim();

  if (!trimmed) {
    return { isValid: false, error: 'Email is required' };
  }

  if (trimmed.length > 254) {
    return { isValid: false, error: 'Email is too long' };
  }

  // Split by @ - must have exactly one @
  const parts = trimmed.split('@');
  if (parts.length !== 2) {
    return { isValid: false, error: 'Invalid email format' };
  }

  const localPart = parts[0];
  const domainPart = parts[1];

  if (!localPart || localPart.length > 64) {
    return { isValid: false, error: 'Invalid email format' };
  }

  if (!domainPart || !domainPart.includes('.')) {
    return { isValid: false, error: 'Invalid email format' };
  }

  // Valid characters in local part: alphanumeric + !#$%&'*+/=?^_`{|}~.-
  const validLocalChars = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+$/;
  if (!validLocalChars.test(localPart)) {
    return { isValid: false, error: 'Invalid email format' };
  }

  // Valid characters in domain: alphanumeric + . + -
  const validDomainChars = /^[a-zA-Z0-9.-]+$/;
  if (!validDomainChars.test(domainPart)) {
    return { isValid: false, error: 'Invalid email format' };
  }

  return { isValid: true };
}

/**
 * Validate a phone number (US and international)
 * @param phone - The phone number to validate
 * @returns Validation result with normalized value and error if invalid
 */
export function validatePhone(phone: string): { isValid: boolean; error?: string; normalized?: string } {
  const trimmed = phone.trim();

  if (!trimmed) {
    return { isValid: false, error: 'Phone number is required' };
  }

  // Extract digits
  const digits = trimmed.replace(/\D/g, '');

  if (digits.length < 10) {
    return { isValid: false, error: 'Phone number must have at least 10 digits' };
  }

  if (digits.length > 15) {
    return { isValid: false, error: 'Phone number is too long' };
  }

  // Normalize: add +1 for 10-digit US/Canada numbers, otherwise just add +
  let normalized: string;
  if (digits.length === 10) {
    normalized = `+1${digits}`;
  } else {
    normalized = `+${digits}`;
  }

  return { isValid: true, normalized };
}

/**
 * Validate a username
 * @param username - The username to validate
 * @returns Validation result with error if invalid
 */
export function validateUsername(username: string): { isValid: boolean; error?: string } {
  const trimmed = username.trim();

  if (!trimmed) {
    return { isValid: false, error: 'Username is required' };
  }

  if (trimmed.length < 3) {
    return { isValid: false, error: 'Username must be at least 3 characters' };
  }

  if (trimmed.length > 64) {
    return { isValid: false, error: 'Username is too long' };
  }

  return { isValid: true };
}

/**
 * Validate credential based on detected type
 * @param input - The user input
 * @returns Full validation result including type, validity, and normalized value
 */
export function validateCredential(input: string): CredentialValidationResult {
  const type = detectCredentialType(input);

  switch (type) {
    case CredentialType.Email: {
      const result = validateEmail(input);
      return {
        type,
        isValid: result.isValid,
        error: result.error,
        normalizedValue: input.trim().toLowerCase(),
      };
    }
    case CredentialType.Phone: {
      const result = validatePhone(input);
      return {
        type,
        isValid: result.isValid,
        error: result.error,
        normalizedValue: result.normalized,
      };
    }
    case CredentialType.Username:
    default: {
      const result = validateUsername(input);
      return {
        type,
        isValid: result.isValid,
        error: result.error,
        normalizedValue: input.trim().toLowerCase(),
      };
    }
  }
}

/**
 * Check password against complexity requirements
 * @param password - The password to check
 * @returns Password requirements status
 */
export function checkPasswordRequirements(password: string): PasswordRequirements {
  return {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*()_+\-=\[\]{}|;':",./<>?~`]/.test(password),
  };
}

/**
 * Calculate password strength based on requirements met
 * @param requirements - The password requirements status
 * @returns Password strength level (0-4)
 */
export function calculatePasswordStrength(requirements: PasswordRequirements): PasswordStrength {
  const met = Object.values(requirements).filter(Boolean).length;

  if (met <= 1) return PasswordStrength.VeryWeak;
  if (met === 2) return PasswordStrength.Weak;
  if (met === 3) return PasswordStrength.Fair;
  if (met === 4) return PasswordStrength.Strong;
  return PasswordStrength.VeryStrong;
}

/**
 * Get human-readable label for password strength
 * @param strength - The password strength level
 * @returns Human-readable strength label
 */
export function getPasswordStrengthLabel(strength: PasswordStrength): string {
  switch (strength) {
    case PasswordStrength.VeryWeak:
      return 'Very Weak';
    case PasswordStrength.Weak:
      return 'Weak';
    case PasswordStrength.Fair:
      return 'Fair';
    case PasswordStrength.Strong:
      return 'Strong';
    case PasswordStrength.VeryStrong:
      return 'Very Strong';
    default:
      return 'Unknown';
  }
}

/**
 * Validate password and get full validation result
 * @param password - The password to validate
 * @returns Full password validation result
 */
export function validatePassword(password: string): PasswordValidationResult {
  const requirements = checkPasswordRequirements(password);
  const strength = calculatePasswordStrength(requirements);
  const strengthLabel = getPasswordStrengthLabel(strength);

  // Password is valid if all requirements are met
  const isValid = Object.values(requirements).every(Boolean);

  return {
    requirements,
    strength,
    strengthLabel,
    isValid,
  };
}

/**
 * Get credential type display label
 * @param type - The credential type
 * @returns Human-readable label for the credential type
 */
export function getCredentialTypeLabel(type: CredentialType): string {
  switch (type) {
    case CredentialType.Email:
      return 'Email detected';
    case CredentialType.Phone:
      return 'Mobile detected';
    case CredentialType.Username:
      return 'Username detected';
    default:
      return '';
  }
}
